import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper, Button, Chip, Skeleton } from '@mui/material';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import StorefrontIcon from '@mui/icons-material/Storefront';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SectionOrnament from './SectionOrnament';

export interface BusinessAd {
  id: string;
  businessName: string;
  tagline: string;
  category: string;
  bannerUrl: string;
  logoUrl?: string;
  description: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  website?: string;
  address?: string;
  mapUrl?: string;
  offer?: string;
  openingHours?: string;
  established?: string;
  galleryImages?: string[];
  youtubeVideos?: string[];  // YouTube video URLs
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    youtube?: string;
    twitter?: string;
  };
  products?: { name: string; image: string; price?: string; description?: string }[];
  highlights?: { icon: string; title: string; description: string }[];
  isActive: boolean;
  subscription?: 'free' | '6months' | '1year';
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  viewCount?: number;
}

// No more mock data — all business promotions are loaded from Firestore

const categoryColors: Record<string, string> = {
  Shopping: '#e91e63',
  Restaurant: '#ff9800',
  'Pooja Items': '#4caf50',
  Jewellery: '#9c27b0',
  Hotel: '#2196f3',
  Travel: '#00bcd4',
  Services: '#607d8b',
};

export default function BusinessPromotions() {
  const [ads, setAds] = useState<BusinessAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [sectionHidden, setSectionHidden] = useState(false);

  useEffect(() => {
    // Real-time listener for section visibility
    const unsubVisibility = onSnapshot(
      doc(db, 'homepage_content', 'business_promotions_section'),
      (snap) => {
        setSectionHidden(snap.exists() && snap.data()?.visible === false);
      },
      (err) => console.error('Error listening to section visibility:', err)
    );

    // Real-time listener for business ads
    const unsubAds = onSnapshot(
      collection(db, 'business_promotions'),
      (snapshot) => {
        const data = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() } as BusinessAd))
          .filter(ad => ad.isActive)
          .sort((a, b) => ((b as any).createdAt || '').localeCompare((a as any).createdAt || ''));
        setAds(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to business promotions:', err);
        setLoading(false);
      }
    );

    return () => {
      unsubVisibility();
      unsubAds();
    };
  }, []);

  const handleViewMore = (ad: BusinessAd) => {
    // Open business detail page using slug-based URL
    const slug = ad.businessName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const url = `/promotions/${slug}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (sectionHidden) return null;
  if (!loading && ads.length === 0) return null;

  return (
    <Box sx={{
      mb: 8, borderRadius: { xs: 4, md: 5 }, overflow: 'hidden',
      background: 'linear-gradient(160deg, #1a0f2e 0%, #2d1b4e 35%, #3a2560 60%, #251845 85%, #1a0f2e 100%)',
      p: { xs: 3, md: 5 }, position: 'relative',
      border: '1px solid rgba(156,39,176,0.15)',
      boxShadow: '0 30px 80px rgba(26,15,46,0.5)',
    }}>
      {/* Ambient glows */}
      <Box sx={{ position: 'absolute', top: -80, right: -60, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(156,39,176,0.1) 0%, transparent 65%)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: -70, left: -50, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(233,30,99,0.06) 0%, transparent 65%)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', top: '40%', left: '60%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,152,0,0.05) 0%, transparent 60%)', pointerEvents: 'none' }} />
      <SectionOrnament variant="om" opacity={0.04} size={90} color="#ce93d8" repeat={3} offset={-80} blendMode="normal" />

      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box sx={{ width: 4, height: 28, background: 'linear-gradient(180deg, #ce93d8, #9c27b0)', borderRadius: 2, boxShadow: '0 0 12px rgba(206,147,216,0.3)' }} />
        <Typography sx={{ color: 'rgba(206,147,216,0.8)', fontWeight: 800, letterSpacing: 3, fontSize: 11, textTransform: 'uppercase' }}>
          FEATURED BUSINESSES
        </Typography>
      </Box>
      <Typography variant="h4" sx={{
        fontWeight: 800, mb: 0.5,
        background: 'linear-gradient(135deg, #f3e5f5, #ce93d8, #ba68c8)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>
        Trusted Local Services
      </Typography>
      <Typography sx={{ color: 'rgba(206,147,216,0.55)', mb: 4, maxWidth: 580, fontSize: { xs: '0.9rem', md: '1.05rem' } }}>
        Explore top-rated businesses, shops, and services handpicked for quality and reliability.
      </Typography>

      {/* Ad banners grid */}
      {loading ? (
        <Grid container spacing={2.5}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={260} sx={{ borderRadius: 4, bgcolor: 'rgba(206,147,216,0.08)' }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2.5}>
          {ads.map((ad, idx) => {
            const catColor = categoryColors[ad.category] || '#ce93d8';
            return (
              <Grid item xs={12} sm={6} key={ad.id}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 4, overflow: 'hidden', position: 'relative',
                    height: { xs: 280, md: 300 },
                    border: '1px solid rgba(156,39,176,0.12)',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: `0 24px 60px rgba(0,0,0,0.5), 0 0 40px ${catColor}20`,
                      borderColor: `${catColor}50`,
                      '& .promo-img': { transform: 'scale(1.1)', filter: 'brightness(0.4)' },
                      '& .promo-cta': { background: catColor, color: '#fff', transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${catColor}40` },
                      '& .promo-overlay': { opacity: 1 },
                    },
                  }}
                  onClick={() => handleViewMore(ad)}
                >
                  {/* Banner image */}
                  <Box
                    className="promo-img"
                    sx={{
                      position: 'absolute', inset: 0,
                      backgroundImage: `url(${ad.bannerUrl})`,
                      backgroundSize: 'cover', backgroundPosition: 'center',
                      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      filter: 'brightness(0.55)',
                    }}
                  />
                  {/* Gradient overlay */}
                  <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(26,15,46,0.3) 0%, rgba(26,15,46,0.5) 40%, rgba(26,15,46,0.92) 100%)' }} />
                  {/* Top accent */}
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${catColor}, transparent 70%)`, opacity: 0.8, zIndex: 2 }} />

                  {/* Sponsored badge */}
                  <Box sx={{
                    position: 'absolute', top: 12, right: 12, zIndex: 3,
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    px: 1.2, py: 0.3, borderRadius: 5,
                    background: 'rgba(0,0,0,0.5)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}>
                    <VerifiedIcon sx={{ fontSize: 12, color: '#4caf50' }} />
                    <Typography sx={{ fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.7)', letterSpacing: 1, textTransform: 'uppercase' }}>
                      Sponsored
                    </Typography>
                  </Box>

                  {/* Content */}
                  <Box sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: { xs: 2.5, md: 3 } }}>
                    {/* Top: category chip */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={<StorefrontIcon sx={{ fontSize: 14, color: `${catColor} !important` }} />}
                        label={ad.category}
                        size="small"
                        sx={{
                          background: 'rgba(255,255,255,0.1)',
                          backdropFilter: 'blur(8px)',
                          border: `1px solid ${catColor}30`,
                          color: catColor,
                          fontWeight: 700, fontSize: 11,
                          '& .MuiChip-icon': { ml: 0.5 },
                        }}
                      />
                      {ad.offer && (
                        <Chip
                          icon={<LocalOfferIcon sx={{ fontSize: 13, color: '#ffd54f !important' }} />}
                          label={ad.offer}
                          size="small"
                          sx={{
                            background: 'rgba(255,213,79,0.12)',
                            backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,213,79,0.2)',
                            color: '#ffd54f',
                            fontWeight: 700, fontSize: 10,
                            '& .MuiChip-icon': { ml: 0.5 },
                          }}
                        />
                      )}
                    </Box>

                    {/* Bottom: name, tagline, CTA */}
                    <Box>
                      <Typography sx={{
                        color: '#fff', fontWeight: 900,
                        fontSize: { xs: '1.2rem', md: '1.4rem' },
                        mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.6)',
                        lineHeight: 1.3,
                      }}>
                        {ad.businessName}
                      </Typography>
                      <Typography sx={{
                        color: 'rgba(255,255,255,0.65)', fontSize: { xs: '0.85rem', md: '0.95rem' },
                        mb: 2, lineHeight: 1.5,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {ad.tagline}
                      </Typography>
                      <Button
                        className="promo-cta"
                        variant="outlined"
                        size="small"
                        endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => { e.stopPropagation(); handleViewMore(ad); }}
                        sx={{
                          borderRadius: 3, textTransform: 'none',
                          fontWeight: 700, fontSize: 13,
                          color: catColor,
                          borderColor: `${catColor}50`,
                          background: `${catColor}10`,
                          px: 2.5, py: 0.8,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: catColor,
                            color: '#fff',
                            borderColor: catColor,
                          },
                        }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Bottom note */}
      <Box sx={{
        mt: 4, pt: 3, borderTop: '1px solid rgba(156,39,176,0.12)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1,
      }}>
        <StorefrontIcon sx={{ fontSize: 16, color: 'rgba(206,147,216,0.35)' }} />
        <Typography sx={{ color: 'rgba(206,147,216,0.35)', fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>
          Want to promote your business here? Contact the temple office for advertising opportunities.
        </Typography>
      </Box>
    </Box>
  );
}
